import styled from 'styled-components'
import { tokens } from '../../styles/tokens'

interface Props {
  page: number
  total: number
  perPage: number
  onChange: (page: number) => void
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`

const Info = styled.p`
  font-size: 12px;
  color: ${tokens.purple200};
`

const Controls = styled.div`
  display: flex;
  gap: 4px;
`

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$active ? tokens.purple400 : tokens.purple100)};
  background: ${p => (p.$active ? tokens.purple50 : 'white')};
  color: ${p => (p.$active ? tokens.purple600 : tokens.purple400)};
  font-size: 12px;
  font-weight: ${p => (p.$active ? 500 : 400)};
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
  transition: all 0.1s;

  &:hover:not(:disabled) {
    background: ${tokens.purple50};
    border-color: ${tokens.purple400};
    color: ${tokens.purple600};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export function Pagination({ page, total, perPage, onChange }: Props) {
  const totalPages = Math.ceil(total / perPage)
  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  if (totalPages <= 1) return null

  // Build page range with ellipsis. Ellipsis items are keyed by the page after the gap
  // so the key is stable across renders for the same pagination state.
  type RangeItem = number | { key: string; ellipsis: true }
  const range: RangeItem[] = []
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      range.push(i)
    } else if (typeof range[range.length - 1] === 'number') {
      range.push({ key: `ellipsis-after-${range[range.length - 1] as number}`, ellipsis: true })
    }
  }

  return (
    <Wrapper>
      <Info>
        Showing {from}–{to} of {total}
      </Info>
      <Controls>
        <PageButton onClick={() => onChange(page - 1)} disabled={page === 1}>
          ←
        </PageButton>
        {range.map(p =>
          typeof p !== 'number' ? (
            <PageButton key={p.key} disabled>
              …
            </PageButton>
          ) : (
            <PageButton key={`page-${p}`} $active={p === page} onClick={() => onChange(p)}>
              {p}
            </PageButton>
          )
        )}
        <PageButton onClick={() => onChange(page + 1)} disabled={page === totalPages}>
          →
        </PageButton>
      </Controls>
    </Wrapper>
  )
}
